// @ts-ignore
const styles = ':host{--modal-body-padding-inline:1.5rem}.modal-body{flex-grow:1;overflow-x:hidden;overflow-y:auto;padding:0 var(--modal-body-padding-inline,1.5rem)}.modal-body::slotted(skapa-modal-edge){display:block;margin-left:calc(var(--modal-body-padding-inline, 1.5rem)*-1);margin-right:calc(var(--modal-body-padding-inline, 1.5rem)*-1);width:calc(100% + var(--modal-body-padding-inline, 1.5rem)*2)}@media (min-width:37.5em){:host(:not([size=small])){--modal-body-padding-inline:2rem}}@media (min-width:75em){:host .modal-body{--modal-body-padding-inline:2.5rem}}:host{--skapa-modal-transition-duration:100ms;--skapa-modal-transition-function:cubic-bezier(0.9,0,1,1);bottom:0;left:0;position:fixed;right:0;top:0;transition:visibility var(--skapa-modal-transition-duration) step-end;visibility:hidden;z-index:3}:host([open]){--skapa-modal-transition-duration:200ms;--skapa-modal-transition-function:cubic-bezier(0.8,0,0,1);pointer-events:unset;transition-timing-function:step-start;visibility:visible}:host([open]) .backdrop{opacity:1}.backdrop{background:rgba(var(--colour-static-black,17,17,17),.4);bottom:0;left:0;opacity:0;position:absolute;right:0;top:0;transition:opacity var(--skapa-modal-transition-duration) var(--skapa-modal-transition-function)}*{box-sizing:border-box}slot{display:block}.modal{background:rgb(var(--colour-elevation-2,255,255,255));display:none;flex-basis:auto;flex-direction:column;min-height:0;overflow:hidden;visibility:hidden;z-index:1}.modal.visible,:host([open]) .modal{display:flex}.modal.visible{visibility:visible}slot[name=header]{z-index:1}@media (prefers-reduced-motion){:host{--skapa-modal-transition-duration:1ms!important}}';
import SkapaElement from '@ingka/skapa-webc-element';
import { lockBody, releaseBody } from './utils/scroll-lock.js';
import { html } from '@ingka/webc-tools';
import { SkapaFocusLock } from '@ingka/focus-lock-shared';
let template;
function getTemplate() {
    if (!template) {
        template = document.createElement('template');
        template.innerHTML = html `
      <style>${styles}</style>
      <div class="backdrop"></div>
    `;
    }
    return template;
}
export class ModalAbstract extends SkapaElement {
    constructor(finalTemplate) {
        super();
        this.reportCloseAnimationFinished = (ev) => {
            if (!this.open && ev.target === this) {
                cancelAnimationFrame(this.__animationHelperID);
                this.panel.classList.remove('visible');
                this.dispatchEvent(new Event('closed'));
            }
        };
        this.handleEscapeRequest = () => {
            const closeEvent = new CustomEvent('closerequest');
            this.dispatchEvent(closeEvent);
            if (!closeEvent.defaultPrevented)
                this.open = false;
        };
        this.__animationHelperID = -1;
        this.updateAssitiveTools = () => {
            cancelAnimationFrame(this.__animationHelperID);
            // Disable automatic focus return if requested.
            this.focusTrap.autoFocusReturn = !this.noAutoReturn;
            // Only set open internal state of the modal if its connected to the DOM.
            if (this.open && this.isConnected) {
                // This is required to ensure chrome and firefox calculates the transform value before the `visible` class is applied. Which class triggers the animation.
                const initialTransform = window.getComputedStyle(this).transform;
                // Let's wrap the dialog launch tasks.
                const lockAndAnimateDialog = () => {
                    // Setting the `visible` class on the panel will trigger its opening animation.
                    // It has to be added a one frame late.
                    this.panel.classList.add('visible');
                    this.enableFocuslock();
                    // The body manipulation must come after the focusTrap activation otherwise the focus return may be lost in some browsers (Safari).
                    // Lock the document's body element and disable scrollbar.
                    if (!this.unlockedScroll)
                        lockBody(this);
                    else
                        releaseBody(this);
                };
                // If the focus lock is requested to be disabled, then it should be done immediately.
                if (this.freeFocus)
                    this.focusTrap.active = false;
                // Turning it on should wait until the Styles and DOM elements have safely been mounted / calculated.
                this.__animationHelperID = requestAnimationFrame(lockAndAnimateDialog);
            }
            else {
                releaseBody(this);
                this.disableFocusLock();
            }
        };
        this.gatherProperties(['open', 'freeFocus', 'unlockedScroll', 'noAutoReturn'], this);
        const shadow = this.attachShadow({ mode: 'open' });
        shadow.append(getTemplate().content.cloneNode(true), finalTemplate.content.cloneNode(true));
        this.backdrop = shadow.querySelector('div.backdrop');
        this.panel = shadow.querySelector('div.modal');
        this.focusTrap = new SkapaFocusLock(this);
        this.focusTrap.autoFocus = true;
    }
    connectedCallback() {
        super.connectedCallback();
        // Probably only set these if the modal is open
        this.setAttribute('aria-hidden', (!this.open).toString());
        this.backdrop.addEventListener('click', this.handleEscapeRequest);
        this.addEventListener('transitionend', this.reportCloseAnimationFinished);
        this.updateAssitiveTools();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.backdrop.removeEventListener('click', this.handleEscapeRequest);
        this.updateAssitiveTools();
        this.removeEventListener('transitionend', this.reportCloseAnimationFinished);
    }
    get open() {
        return this.hasAttribute('open');
    }
    set open(val) {
        this.toggleAttribute('open', !!val);
    }
    get freeFocus() {
        return this.hasAttribute('free-focus');
    }
    set freeFocus(toggle) {
        this.toggleAttribute('free-focus', !!toggle);
    }
    get noAutoReturn() {
        return this.hasAttribute('no-auto-return');
    }
    set noAutoReturn(toggle) {
        this.toggleAttribute('no-auto-return', !!toggle);
    }
    get unlockedScroll() {
        return this.hasAttribute('unlocked-scroll');
    }
    set unlockedScroll(toggle) {
        this.toggleAttribute('unlocked-scroll', !!toggle);
    }
    enableFocuslock() {
        this.focusTrap.active = !this.freeFocus;
        this.focusTrap.removeEventListener('skapa-focus-lock-escape-hotkey', this.handleEscapeRequest); // make sure to prevent duplicate listeners.
        this.focusTrap.addEventListener('skapa-focus-lock-escape-hotkey', this.handleEscapeRequest);
    }
    disableFocusLock() {
        this.focusTrap.active = false;
        this.focusTrap.removeEventListener('skapa-focus-lock-escape-hotkey', this.handleEscapeRequest);
    }
    attributeChangedCallback(name, oldVal, newVal) {
        if (name === 'open') {
            const isOpen = newVal !== null;
            this.setAttribute('role', isOpen ? 'dialog' : 'none'); // This is required for NVDA to refresh itself on second view.
            this.setAttribute('aria-modal', isOpen.toString());
            this.setAttribute('aria-hidden', (!isOpen).toString());
            this.updateAssitiveTools();
        }
        else if (['no-auto-return', 'free-focus', 'unlocked-scroll'].includes(name)) {
            this.updateAssitiveTools();
        }
    }
}
ModalAbstract.observedAttributes = ['open', 'free-focus', 'unlocked-scroll', 'no-auto-return'];
